import { createDirectStore, } from 'direct-vuex';
import Vue from 'vue';
import Vuex from 'vuex';
import { AccountStatementResponse, Address, AuthenticatedCustomerProfileDetails, AuthenticatedCustomerPropertiesDetails, BusinessPartner, BusinessPartnerContractAccount, Contract, ContractAccount, ContractAccountDetails, ContractPropertyDetailsResponse, DomainMeterDetails, MeterRead, PaymentDetails, RequestParams, WaterUsageResponse, } from '~/api/self-serve-client';
import { generateDateFormatForApi, validDirectDebitDates, } from '~/utils/dateFormatingMethods';
import { groupBy, } from '~/utils/linq';
import { CustomerBasicDetails, } from '~/utils/models';
import { isDate, } from '~/utils/models/isObject';
import { Result, loadResultedData, mapCompleted, } from '~/utils/models/result';
import { localStorageValue, } from '~/utils/store';
import alertModule from '~/utils/store/alert-module';
import globalConfigModule from '~/utils/store/global-config-module';
import customContent from './custom-content';
import resetPassword from './reset-password';
import changeEmail from '../change-email-address/store';
import directDebit from '../direct-debit/store';
import findCustomerNumber from '../find-customer-number/store';
import forgotEmail from '../forgot-email/store';
import meterRead from '../meter-read/store';
import moveOut from '../move-out/store';
import moveWithin from '../move-within/store';
import payBill from '../pay-bill/store';
import profileUpdate from '../profile/store';
import register from '../register/store';

Vue.use(Vuex);

const AccessTokenExpirationStorageKey = 'ae';

// Utility function to generate correlation headers
const getCorrelationHeaders = (correlationGuid: string | null): RequestParams => {
	if (correlationGuid) {
		const headers = { 'X-Correlation-ID': correlationGuid, } as Record<string, string>;
		return { headers, } as RequestParams;
	}
	return {} as RequestParams;
};

interface SupplyAddressDetailsRequest {
	contractNumber: string;
	journeyName: string;
	requestParams?: RequestParams;
}

interface ContractAccountDetailRequest {
	contractAccountNumber: string;
	journeyName: string;
	requestParams?: RequestParams;
}

interface ContractDetailRequest {
	contractNumber: string;
	requestParams?: RequestParams;
}

interface ContractAccountPaymentDetailsRequest {
	selectContractAccount: string;
	requestParams?: RequestParams;
}

interface IdendifyCustomerRequest {
	fetchTime: boolean;
	journeyName: string;
}

export interface Message {
	type: 'Success' | 'Warning' | 'Error';
	body: unknown;
	title?: string;
	status: 'initial' | 'unread' | 'read';
}

export interface MeterDetails {
	id: string;
	category: string;
	lastReading: number | null;
	lastReadDate: Date | null;
}

export interface CustomerMeterDetails {
	id: string;
	category: string;
	lastReading: number | null;
	lastReadDate: Date | null;
	contractId: string;
	isPropertyActive: boolean;
}

export interface SelectedWaterUsageDetails {
	meterID: string;
	meterCategory: string;
	contractId: string;
}

export interface OverlayData {
	navigateTo?: string;
	message?: string;
	isTimerOverlay?: boolean;
	isDisplayNewFeature?: boolean;
}

interface Tracker {
	get(key: 'clientId'): string;
}

interface GA {
	getAll(): Tracker[];
}

function windowHasGoogleAnalyics<WindowType extends Window>(window: WindowType): window is WindowType & { ga: GA, } {
	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
	return typeof (window as any).ga !== 'undefined';
}

function fetchUID() {
	try {
		return windowHasGoogleAnalyics(window) ? window.ga.getAll()[0]?.get('clientId') : undefined;
	} catch {
		return undefined;
	}
}

function remainingTimeAuthenticatedInMilliseconds(accessTokenExpiration: Date | null) {
	if (!accessTokenExpiration) {
		return 0;
	}

	return (accessTokenExpiration.valueOf() - new Date().valueOf());
}

const accessTokenExpirationValue = localStorageValue(AccessTokenExpirationStorageKey, isDate, () => null, JSON.stringify, (v) => new Date(JSON.parse(v)));

function initialiseAccessTokenExpiration() {
	return accessTokenExpirationValue.get();
}

export type Results<T> = { state: 'initial', } | { state: 'loading', } | { state: 'completed', result: T, } | { state: 'errored', };

const initialState = () => {
	const accessTokenExpiration = initialiseAccessTokenExpiration();
	return {
		showSpinner: false,
		isOverlayVisible: false,
		isOverlayWithSlotVisible: false,
		overlayMessage: '',
		navigateTo: '',
		isTimerOverlay: false,
		isDisplayNewFeature: false,
		userDetails: null as CustomerBasicDetails | null,
		businessPartnerDetails: null as BusinessPartner | null,
		selectContractAccount: '',
		selectedContractAccountNumber: '',
		selectedContractAccountSmStatus: {} as { smartMeter: boolean, smartMeterExchangeState: string | null, },
		isPropertyActive: true as boolean,
		message: null as Message | null,
		postLoginPath: null as string | null,
		accessTokenExpiration,
		bpNumberFromLoginResponse: '',
		// if our stored date isn't valid, then we know we've prefetched, because there is nothing to prefetch
		prefetchedCustomerDetails: accessTokenExpiration == null,
		customerProfile: { state: 'initial', } as Result<AuthenticatedCustomerProfileDetails>,
		contractAccountDetails: { state: 'initial', } as Result<ContractAccountDetails>,
		contractAccountData: { state: 'initial', } as Results<ContractAccount>,
		contractData: { state: 'initial', } as Results<Contract>,
		contractAccountPaymentDetails: { state: 'initial', } as Results<PaymentDetails>,
		contractSupplyAddressDetail: { state: 'initial', } as Results<Address>,
		contractMeterDetailsDetail: { state: 'initial', } as Results<DomainMeterDetails>,
		meterReadsDetail: { state: 'initial', } as Results<MeterRead[]>,
		correspondenceAddress: { state: 'initial', } as Results<Address>,
		contractPropertyDetails: { state: 'initial', } as Result<ContractPropertyDetailsResponse>,
		contractClientMeterDetails: { state: 'initial', } as Result<CustomerMeterDetails[]>,
		accountStatement: { state: 'initial', } as Result<AccountStatementResponse>,
		switchAccountDetails: { state: 'initial', } as Result<AuthenticatedCustomerPropertiesDetails[]>,
		waterUsageDetails: { state: 'initial', } as Result<WaterUsageResponse>,
		recommendedAmount: null as null | number,
		isToleranceLimitNull: false,
		nextDirectDebitDate: null as null | Date,
		dashboardCorrelationGuid: null as null | string,
		selectedWaterUsageDetails: {
			meterID: '',
			meterCategory: '',
			contractId: '',
		} as SelectedWaterUsageDetails,
	};
};

// eslint-disable-next-line @typescript-eslint/unbound-method
const {
	store,
	rootActionContext,
} = createDirectStore({
	state: initialState,
	getters: {
		isAuthenticated(state) {
			return !!state.businessPartnerDetails && !!state.accessTokenExpiration && !isNaN(state.accessTokenExpiration.valueOf()) && remainingTimeAuthenticatedInMilliseconds(state.accessTokenExpiration) > 0;
		},
		availableMessage(state) {
			if (state.message && state.message.status === 'unread') {
				return state.message;
			}
			return undefined;
		},
		canMoveOut(state) {
			if (state.contractPropertyDetails.state === 'completed' && state.contractPropertyDetails.result.moveOutDate) {
				return false;
			}
			return true;
		},
		// Below code is Dashboard specific only as we are working on performance stuff and API are decoupled only for the Dashboard and we want that rest of the app will not be impacted due to this change
		canMoveOutOnDashboard(state) {
			if (state.contractData.state === 'completed' && state.contractData.result.moveOutDate) {
				return false;
			}
			return true;
		},
		meterList(state) {
			return mapCompleted(state.contractClientMeterDetails, (details): MeterDetails[] => {
				if (details.length < 1) {
					return [];
				}

				const getProperty = details.find((item) => item.isPropertyActive === true) || details[0];
				const groupedMeters = groupBy(details, 'contractId');
				const y = groupedMeters.find(({ key, }) => key === getProperty.contractId);
				if (y === undefined) {
					return [];
				}

				return y.value;
			});
		},
		meterListV2(state) {
			if (state.contractMeterDetailsDetail.state === 'completed') {
				return state.contractMeterDetailsDetail;
			}
			return { state: state.contractMeterDetailsDetail.state, };
		},
		customerMeterList(state) {
			return mapCompleted(state.contractClientMeterDetails, (details) => details);
		},
		contractId(state) {
			return state.contractAccountDetails.state === 'completed' && state.contractAccountDetails.result.contracts[0];
		},
		contractAccountPaymentMethod(state) {
			return state.contractAccountDetails.state === 'completed' && state.contractAccountDetails.result.paymentMethod;
		},
	},
	mutations: {
		showSpinner(state) {
			state.showSpinner = true;
		},
		hideSpinner(state) {
			state.showSpinner = false;
		},
		showOverlay(state, data: OverlayData) {
			state.isOverlayVisible = true;
			state.overlayMessage = data.message ? data.message : '';
			state.navigateTo = data.navigateTo ? data.navigateTo : '';
			state.isTimerOverlay = data.isTimerOverlay ? data.isTimerOverlay : false;
			state.isDisplayNewFeature = !!data.isDisplayNewFeature;
		},
		closeOverlay(state) {
			state.isOverlayVisible = false;
		},
		showOverlayWithSlot(state) {
			state.isOverlayWithSlotVisible = true;
		},
		closeOverlayWithSlot(state) {
			state.isOverlayWithSlotVisible = false;
		},
		updateOverlayMessage(state, message) {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			state.overlayMessage = message;
		},
		updateCustomerDetails(state, userDetails: CustomerBasicDetails) {
			state.userDetails = userDetails;
		},
		updatebusinessPartnerDetails(state, businessPartnerDetails: BusinessPartner) {
			state.businessPartnerDetails = businessPartnerDetails;
		},
		updateCorrespondenceAddress(state, correspondenceAddress: Results<Address>) {
			state.correspondenceAddress = correspondenceAddress;
		},
		updateCustomerEmail(state, emailAddress: string) {
			if (state.userDetails) {
				state.userDetails.emailAddress = emailAddress;
			}
		},
		setContractAccount(state, contractAccountNumber: string) {
			state.selectContractAccount = contractAccountNumber;
		},
		setselectedContractAccountV2(state, selectedContractAccountNumber: string) {
			state.selectedContractAccountNumber = selectedContractAccountNumber;
		},
		selectedContractAccountSmStatus(state, selectedContractAccountSmStatus) {
			state.selectedContractAccountSmStatus = { ...selectedContractAccountSmStatus, };
		},
		setPropertyActive(state, isPropertyActive: boolean) {
			state.isPropertyActive = isPropertyActive;
		},
		updateAccessExpiration(state, expiration: Date) {
			state.accessTokenExpiration = expiration;
			accessTokenExpirationValue.set(expiration);
		},
		updateBpNumberFromLogin(state, bpNumber) {
			state.bpNumberFromLoginResponse = bpNumber;
			localStorage.setItem('bpNumberFromLoginResponse', bpNumber);
		},
		updateProfileDetails(state, customerProfile: Result<AuthenticatedCustomerProfileDetails>) {
			state.customerProfile = customerProfile;
			if (customerProfile.state === 'completed' && state.userDetails) {
				state.userDetails.emailAddress = customerProfile.result.emailAddress;
			}
		},
		setContractAccountDetail(state, contractAccountData: Result<ContractAccount>) {
			state.contractAccountData = contractAccountData;
		},
		setContractDetail(state, contractData: Results<Contract>) {
			state.contractData = contractData;
		},
		setContractSupplyAddressDetail(state, contractSupplyAddressDetail: Results<Address>) {
			state.contractSupplyAddressDetail = contractSupplyAddressDetail;
		},
		setContractMeterDetailsDetail(state, contractSupplyAddressDetail: Results<DomainMeterDetails>) {
			state.contractMeterDetailsDetail = contractSupplyAddressDetail;
		},
		setMeterReads(state, meterReadsDetail: Results<MeterRead[]>) {
			state.meterReadsDetail = meterReadsDetail;
		},
		async setContractAccountPaymentDetails(state, contractAccountPaymentDetails: Results<PaymentDetails>) {
			state.contractAccountPaymentDetails = contractAccountPaymentDetails;
			if (contractAccountPaymentDetails.state === 'completed' && contractAccountPaymentDetails.result.futurePaymentSchedule[0] && state.contractAccountData.state === 'completed' && state.contractAccountData.result.paymentMethod === 'D') {
				const validDdDates = validDirectDebitDates;
				let nextDirectDebitDate = new Date(contractAccountPaymentDetails.result.futurePaymentSchedule[0]);
				const comparisonDirectDebitDate = new Date(contractAccountPaymentDetails.result.futurePaymentSchedule[0]);
				while (!validDdDates.includes(nextDirectDebitDate.getDate())) {
					nextDirectDebitDate.setDate(nextDirectDebitDate.getDate() - 1);
				}

				const { result: { directDebitDate, }, } = await Vue.selfServeApi.directDebitDirectDebitDateCalculatorList({
					date: nextDirectDebitDate.toDateString(),
				});
				const newDate = new Date(directDebitDate);

				if (new Date(newDate.toDateString()) >= new Date(comparisonDirectDebitDate.toDateString())) {
					state.nextDirectDebitDate = newDate;
					return;
				}

				nextDirectDebitDate = new Date(comparisonDirectDebitDate);

				while (!validDdDates.includes(nextDirectDebitDate.getDate())) {
					nextDirectDebitDate.setDate(nextDirectDebitDate.getDate() + 1);
				}

				const { result, } = await Vue.selfServeApi.directDebitDirectDebitDateCalculatorList({
					date: nextDirectDebitDate.toDateString(),
				});
				state.nextDirectDebitDate = new Date(result.directDebitDate);
			} else {
				state.nextDirectDebitDate = null;
			}
		},
		// We have to keep the blow code as this is called from the other journeys apart from Dashboard
		async updateContractAccountDetails(state, contractAccountDetails: Result<ContractAccountDetails>) {
			state.contractAccountDetails = contractAccountDetails;
			if (contractAccountDetails.state === 'completed' && contractAccountDetails.result.paymentMethod === 'D') {
				const validDdDates = validDirectDebitDates;
				let nextDirectDebitDate = new Date(contractAccountDetails.result.paymentScheduleItems[0].paymentDate);
				const comparisonDirectDebitDate = new Date(contractAccountDetails.result.paymentScheduleItems[0].paymentDate);
				while (!validDdDates.includes(nextDirectDebitDate.getDate())) {
					nextDirectDebitDate.setDate(nextDirectDebitDate.getDate() - 1);
				}

				const { result: { directDebitDate, }, } = await Vue.selfServeApi.directDebitDirectDebitDateCalculatorList({
					date: nextDirectDebitDate.toDateString(),
				});
				const newDate = new Date(directDebitDate);

				if (new Date(newDate.toDateString()) >= new Date(comparisonDirectDebitDate.toDateString())) {
					state.nextDirectDebitDate = newDate;
					return;
				}

				nextDirectDebitDate = new Date(comparisonDirectDebitDate);

				while (!validDdDates.includes(nextDirectDebitDate.getDate())) {
					nextDirectDebitDate.setDate(nextDirectDebitDate.getDate() + 1);
				}

				const { result, } = await Vue.selfServeApi.directDebitDirectDebitDateCalculatorList({
					date: nextDirectDebitDate.toDateString(),
				});
				state.nextDirectDebitDate = new Date(result.directDebitDate);
			} else {
				state.nextDirectDebitDate = null;
			}
		},
		updateClientMeterDetails(state, contractClientMeterDetails: Result<CustomerMeterDetails[]>) {
			state.contractClientMeterDetails = contractClientMeterDetails;
		},
		updateContractPropertyAddress(state, contractPropertyDetails: Result<ContractPropertyDetailsResponse>) {
			state.contractPropertyDetails = contractPropertyDetails;
		},
		updateSwitchAccountDetails(state, switchAccountDetails: Result<AuthenticatedCustomerPropertiesDetails[]>) {
			state.switchAccountDetails = switchAccountDetails;
		},
		updateWaterUsageDetails(state, waterUsageDetails: Result<WaterUsageResponse>) {
			state.waterUsageDetails = waterUsageDetails;
		},
		updatedContactNumber(state, { mobileNumber, telephoneNumber, mobileIsdCode, telephoneIsdCode, }) {
			if (state.customerProfile.state === 'completed') {
				state.customerProfile.result = { ...state.customerProfile.result, mobileNumber, telephoneNumber, mobileIsdCode, telephoneIsdCode, };
			}
		},
		showMessage(state, message: Omit<Message, 'status'>) {
			state.message = {
				...message,
				status: 'initial',
			};
		},
		makeMessageAsRead(state) {
			if (!state.message) {
				return;
			}

			state.message = {
				...state.message,
				status: 'read',
			};
		},
		clearReadMessage(state) {
			if (state.message?.status === 'read') {
				state.message = null;
			}
		},
		prepareAvailableMessage(state) {
			if (state.message?.status === 'initial') {
				state.message = {
					...state.message,
					status: 'unread',
				};
			}
		},
		setPostLoginPath(state, path: string | null) {
			state.postLoginPath = path;
		},
		markPreFetched(state) {
			state.prefetchedCustomerDetails = true;
		},
		updatePaperlessPref(state, paperlessPref:string) {
			if (state.userDetails) {
				state.userDetails.optedForPaperless = paperlessPref;
			}
		},
		updateAccountStatement(state, statements: Result<AccountStatementResponse>) {
			state.accountStatement = statements;
		},
		setSelectedWaterUsageDetails(state, selectedWaterUsageData: SelectedWaterUsageDetails) {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			state.selectedWaterUsageDetails = selectedWaterUsageData;
		},
		setRecommendedAmount(state, recommendedAmount) {
			state.recommendedAmount = recommendedAmount;
		},
		setToleranceLimit(state, isToleranceLimitNull) {
			state.isToleranceLimitNull = isToleranceLimitNull;
		},
		generateDashboardGuid(state) {
			state.dashboardCorrelationGuid = crypto.randomUUID();
		},
		reset(state) {
			accessTokenExpirationValue.remove();
			localStorage.removeItem('bpNumberFromLoginResponse');
			Object.assign(state, initialState());
		},
	},
	actions: {
		async authenticate(context, { emailAddress, password, }: { emailAddress: string, password: string, }) {
			const { commit, state, } = rootActionContext(context);
			const uid = fetchUID();
			commit.generateDashboardGuid();
			const requestParams = getCorrelationHeaders(state.dashboardCorrelationGuid);
			const { result, } = await Vue.selfServeApi.loginCustomerLoginCreate({
				emailId: emailAddress,
				password: password,
				uid,
			}, requestParams);

			commit.updateAccessExpiration(new Date(result.accessTokenExpiration));
			commit.updateBpNumberFromLogin(result.businessPartnerNumber);
			// await dispatch.identifyCustomer({ fetchTime: false, journeyName: journeyName, });
		},
		async identifyCustomer(context, request: IdendifyCustomerRequest) {
			const { commit, dispatch, state } = rootActionContext(context);
			const requestParams = getCorrelationHeaders(state.dashboardCorrelationGuid);
			try {
				if (request.fetchTime) {
					const { result: expiry, } = await Vue.selfServeApi.loginTimeRemainingList();
					commit.updateAccessExpiration(new Date(expiry.accessTokenExpiration));
				}

				const bpNumberFromLoginResponse = localStorage.getItem('bpNumberFromLoginResponse') ?? '';
				const responseData = await Vue.selfServeApi.businessPartnerDetail(bpNumberFromLoginResponse, requestParams);
				commit.updatebusinessPartnerDetails(responseData);

				let contractAccountNumber = null;
				const movedOutAccounts = responseData.contractAccounts.filter((acc) => acc.isMovedOut);
				const notMovedOutAccounts = responseData.contractAccounts.filter((acc) => !acc.isMovedOut);

				const getMostRecentAccount = (accounts: BusinessPartnerContractAccount[], dateKey: string) => {
					return accounts.reduce((latest, account) =>
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						new Date((account as any)[dateKey]) > new Date((latest as any)[dateKey]) ? account : latest, accounts[0]
					);
				};

				if (notMovedOutAccounts.length > 0) {
					contractAccountNumber = getMostRecentAccount(notMovedOutAccounts, 'moveInDate');
				} else if (movedOutAccounts.length > 0) {
					contractAccountNumber = getMostRecentAccount(movedOutAccounts, 'moveOutDate');
				}

				// use commit.setContractAccount below to set the contractAccountNumber in the store that has been used by the existing APIs in the other journeys
				commit.setContractAccount(contractAccountNumber?.number || '');
				if (contractAccountNumber?.number) {
					await dispatch.contractAccountDetail({ contractAccountNumber: contractAccountNumber?.number, journeyName: request.journeyName, requestParams });
				}
			} catch (e) {
				await dispatch.resetAll();
				throw e;
			}
		},

		async contractAccountDetail(context, request: ContractAccountDetailRequest) {
			const { commit, dispatch, } = rootActionContext(context);
			try {
				commit.setContractAccountDetail({ state: 'loading', });
				const responseData = await Vue.selfServeApi.contractAccountDetail(request.contractAccountNumber, request.requestParams);
				await dispatch.setLastLoginTime();
				const selectedContractAccount = responseData.contracts[0].number as string;
				commit.setContractAccountDetail({ state: 'completed', result: responseData, });
				commit.setselectedContractAccountV2(selectedContractAccount);
				commit.selectedContractAccountSmStatus({
					smartMeterExchangeState: responseData.smartMeterExchangeState,
					smartMeter: responseData.contracts[0].smartMeter,
				});
				await dispatch.selectedContractAccount({ contractNumber: selectedContractAccount, journeyName: request.journeyName, requestParams: request.requestParams });
			} catch (e) {
				console.error(e);
			}
		},

		selectedContractAccount(context, request: SupplyAddressDetailsRequest) {
			const { dispatch, state, } = rootActionContext(context);

			void dispatch.contractDetail(request);
			void dispatch.contractAccountPaymentDetailsDetail({ 
				selectContractAccount: state.selectContractAccount, 
				requestParams: request.requestParams 
			});
			void dispatch.contractSupplyAddressDetail(request);
			void dispatch.contractMeterDetailsDetail(request);
		},
		async contractDetail(context, {contractNumber}: ContractDetailRequest) {
			const { commit, } = rootActionContext(context);
			try {
				commit.setContractDetail({ state: 'loading', });
				if (!contractNumber) {
					throw new Error('selectedContractAccountNumber is required');
				}
				const responseData = await Vue.selfServeApi.contractDetail(contractNumber);
				commit.setContractDetail({ state: 'completed', result: responseData, });
			} catch (e) {
				console.error(e);
				commit.setContractDetail({ state: 'errored', });
			}
		},
		async contractAccountPaymentDetailsDetail(context, {selectContractAccount}: ContractAccountPaymentDetailsRequest) {
			const { commit, } = rootActionContext(context);
			try {
				commit.setContractAccountPaymentDetails({ state: 'loading', });
				const responseData = await Vue.selfServeApi.contractAccountPaymentDetailsDetail(selectContractAccount);
				commit.setContractAccountPaymentDetails({ state: 'completed', result: responseData, });
			} catch (e) {
				console.error(e);
				commit.setContractAccountPaymentDetails({ state: 'errored', });
			}
		},
		async contractSupplyAddressDetail(context, {contractNumber, journeyName}: SupplyAddressDetailsRequest) {
			const { commit, } = rootActionContext(context);
			try {
				commit.setContractSupplyAddressDetail({ state: 'loading', });
				const journeyNameHeader = { 'SouthernWater-EServices-JourneyName': journeyName, } as Record<string, string>;
				const requestedParams = { headers: journeyNameHeader } as RequestParams;
				// const requestedParams = { headers: journeyNameHeader, ...requestParams } as RequestParams;
				const responseData = await Vue.selfServeApi.contractSupplyAddressDetail(contractNumber, requestedParams);
				commit.setContractSupplyAddressDetail({ state: 'completed', result: responseData, });
			} catch (e) {
				console.error(e);
				commit.setContractSupplyAddressDetail({ state: 'errored', });
			}
		},
		async contractMeterDetailsDetail(context, {contractNumber}) {
			const { commit, dispatch, } = rootActionContext(context);
			try {
				commit.setContractMeterDetailsDetail({ state: 'loading', });
				const responseData = await Vue.selfServeApi.contractMeterDetailsDetail(contractNumber);
				commit.setContractMeterDetailsDetail({ state: 'completed', result: responseData, });
				await dispatch.meterReads(contractNumber);
			} catch (e) {
				console.error(e);
				commit.setContractMeterDetailsDetail({ state: 'errored', });
			}
		},
		async setCorrespondenceAddress(context, contractAccountNumber: string) {
			const { commit, dispatch, state } = rootActionContext(context);
			try {
				commit.updateCorrespondenceAddress({ state: 'loading', });
				const bpNumberFromLoginResponse = localStorage.getItem('bpNumberFromLoginResponse') ?? '';
				const responseData = await Vue.selfServeApi.businessPartnerCorrespondenceAddressDetail(bpNumberFromLoginResponse);
				commit.updateCorrespondenceAddress({ state: 'completed', result: responseData, });
				void dispatch.updateAccountStatement(contractAccountNumber);
			} catch (e) {
				console.error(e);
				commit.updateCorrespondenceAddress({ state: 'errored', });
			}
		},
		async setLastLoginTime() {
			if (sessionStorage.getItem('isLastLoginTimeSet') !== 'true') {
				try {
					const bpNumberFromLoginResponse = localStorage.getItem('bpNumberFromLoginResponse') ?? '';
					await Vue.selfServeApi.loginSetLastLoginTimeCreate({
						businessPartnerNumber: bpNumberFromLoginResponse
					});
					sessionStorage.setItem('isLastLoginTimeSet', 'true');
				} catch (e) {
					console.error(e);
				}
			}
		},
		async init(context, journeyName: string) {
			const { state, commit, dispatch, } = rootActionContext(context);
			if (state.prefetchedCustomerDetails) {
				return;
			}

			commit.showSpinner();
			try {
				await dispatch.identifyCustomer({ fetchTime: true, journeyName: journeyName, });
			} catch (e) {
				// swallow
			} finally {
				commit.markPreFetched();
				commit.hideSpinner();
			}
		},
		async logout(context) {
			const { state, } = rootActionContext(context);
			if (!state.prefetchedCustomerDetails) {
				return;
			}

			// sessionStorage.removeItem('sessionLoginCount');
			sessionStorage.clear();
			await Vue.selfServeApi.loginTRevokeCreate();
		},
		// Have to keep the setSelectedContractAccount as this method is used from the other journeys apart from Dashboard
		// Have to remove the calling of this method from this file as we are not using it on the loading of the dashboard
		setSelectedContractAccount(context, contractAccountNumber: string) {
			const { dispatch, commit, } = rootActionContext(context);

			commit.setContractAccount(contractAccountNumber);
			void dispatch.updateContractAccountDetails(contractAccountNumber);
			void dispatch.updateContractPropertyDetails(contractAccountNumber);
			void dispatch.updateClientMeterList(contractAccountNumber);
			void dispatch.updateSwitchAccountDetails(contractAccountNumber);
		},
		async updateProfileDetails(context) {
			const { state, commit, } = rootActionContext(context);
			await loadResultedData(state.customerProfile, Vue.selfServeApi.customerDetailsCustomerProfileDetailsList, commit.updateProfileDetails);
		},
		async updateSwitchAccountDetails(context, contractAccountNumber) {
			const { state, commit, dispatch, } = rootActionContext(context);
			await loadResultedData(state.switchAccountDetails, Vue.selfServeApi.customerDetailsCustomerPropertyDetailsList, commit.updateSwitchAccountDetails);
			void dispatch.updateAccountStatement(contractAccountNumber);
		},
		async updateWaterUsageDetails(context, { ContractAccountNumber, ContractId, MeterCategory, MeterId, }: { ContractAccountNumber: string, ContractId: string, MeterCategory: string, MeterId: string, }) {
			const { state, commit, } = rootActionContext(context);
			commit.setSelectedWaterUsageDetails({
				contractId: ContractId,
				meterCategory: MeterCategory,
				meterID: MeterId,
			});
			await loadResultedData(state.waterUsageDetails, () => Vue.selfServeApi.waterUsageViewWaterUsageList({
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				ContractAccountNumber,
				ContractId: ContractId,
				MeterCategory,
				MeterId,
			}), commit.updateWaterUsageDetails);
		},
		async updateContractAccountDetails(context, ContractAccountNumber: string) {
			const { state, commit, } = rootActionContext(context);
			await loadResultedData(state.contractAccountDetails, () => Vue.selfServeApi.customerDetailsContractAccountDetailsList({ ContractAccountNumber, }), commit.updateContractAccountDetails);
		},
		async updateContractPropertyDetails(context, ContractAccountNumber: string) {
			const { state, commit, } = rootActionContext(context);
			await loadResultedData(state.contractPropertyDetails, () => Vue.selfServeApi.customerDetailsContractPropertyDetailsList({ ContractAccountNumber, }), commit.updateContractPropertyAddress);
		},
		async updateAccountStatement(context, contractAccountNumber: string) {
			const { state, commit, } = rootActionContext(context);
			const filteredData = state.switchAccountDetails.state === 'completed' ? state.switchAccountDetails.result.find((item) => item.conractAccountId === contractAccountNumber) : undefined;
			const moveInDate: Date | string = filteredData !== undefined ? new Date(filteredData.moveInDate) : '';
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const latestDate = filteredData !== undefined && !filteredData.isPropertyActive ? new Date(filteredData.moveOutDate!) : new Date();
			let twoYearsAgo = new Date(new Date(latestDate).setFullYear(latestDate.getFullYear() - 2));
			const numberOfYears = new Date(new Date().valueOf() - new Date(moveInDate).valueOf()).getFullYear() - 1970;
			if (numberOfYears < 2) {
				twoYearsAgo = new Date(moveInDate);
			}
			await loadResultedData(
				state.accountStatement,
				() => Vue.selfServeApi.accountStatementViewAccountStatementCreate({
					contractAccountNumber,
					fromDate: generateDateFormatForApi(twoYearsAgo),
					toDate: generateDateFormatForApi(new Date()),
				}),
				commit.updateAccountStatement
			);
		},

		async updateClientMeterList(context, ContractAccountNumber: string) {
			const { state, commit, dispatch, } = rootActionContext(context);
			await loadResultedData(
				state.contractClientMeterDetails,
				() => Vue.selfServeApi.customerDetailsContractAccountMeterDetailsList({ ContractAccountNumber, }).then((x) => {
					const metersDetail: CustomerMeterDetails[] = x.result.contracts
						.map((item) => item.meters.map((subItem): CustomerMeterDetails => ({ ...subItem, lastReading: subItem.lastReading || null, contractId: item.contractId, lastReadDate: subItem.lastReadDate ? new Date(subItem.lastReadDate) : null, isPropertyActive: item.isPropertyActive, })))
						.reduce((acc, val) => {
							acc.push(...val);
							return acc;
						}, []);

					return {
						result: metersDetail,
					};
				}),
				(contractMeterDetails) => {
					commit.updateClientMeterDetails(contractMeterDetails);

					if (contractMeterDetails.state === 'completed' && contractMeterDetails.result.length > 0) {
						const defaultMeter = contractMeterDetails.result[0];
						void dispatch.updateWaterUsageDetails({ ContractAccountNumber, ContractId: defaultMeter.contractId, MeterCategory: defaultMeter.category, MeterId: defaultMeter.id, });
					}
				}
			);
		},

		async meterReads(context, selectedContractAccountNumber: string) {
			const { state, commit, dispatch, } = rootActionContext(context);
			const meterDetail = state.contractMeterDetailsDetail.state === 'completed' ? state.contractMeterDetailsDetail.result.meters[0] : '';
			const meterNumber = meterDetail && meterDetail.number;
			const meterCategory = meterDetail && meterDetail.category;
			const todayDate = generateDateFormatForApi(new Date());
			if (meterDetail && meterNumber) {
				try {
					commit.setMeterReads({ state: 'loading', });
					const responseData = await Vue.selfServeApi.contractMeterReadsDetail(selectedContractAccountNumber, meterNumber, { to: todayDate, });
					const sortedMeterList = responseData.sort((a, b) => (new Date(b.date) as unknown as number) - (new Date(a.date) as unknown as number));
					commit.setMeterReads({ state: 'completed', result: sortedMeterList, });
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					await dispatch.updateWaterUsageDetails({ ContractAccountNumber: state.selectContractAccount, ContractId: selectedContractAccountNumber, MeterCategory: meterCategory!, MeterId: meterNumber, });
				} catch (e: any) {
					if (e.status === 404) {
						commit.setMeterReads({ state: 'completed', result: [], });
					} else {
						console.error(e);
						commit.setMeterReads({ state: 'errored', });
					}
				}
			}
		},

		async getRecommendedAmount(context, { contractId, }) {
			const { commit, } = rootActionContext(context);
			try {
				const { result: { paymentDate, }, } = await Vue.selfServeApi.directDebitPopulateDirectDebitDetailsCreate({ contractNo: contractId, });
				try {
					const result = await Vue.selfServeApi.directDebitSimulateContractDetail(contractId, {
						paymentDate: paymentDate as number,
					});
					commit.setRecommendedAmount(result.recommendedAmount);
					if (result.recommendedAmount === result.minimumAmount && result.recommendedAmount === result.maximumAmount) {
						commit.setToleranceLimit(true);
					}
				} catch (e) {
					console.error(e);
				}
			} catch (e) {
				console.error(e);
			}
		},

		resetAll(context) {
			const { commit, } = rootActionContext(context);
			[
				commit,
				commit.meterRead,
				commit.moveOut,
				commit.payBill,
				commit.register,
				commit.directDebit,
				commit.moveWithin,
				commit.profileUpdate,
				commit.findCustomerNumber,
				commit.forgotEmail,
			].forEach((c) => c.reset());
		},
	},
	modules: {
		alertModule,
		meterRead,
		moveOut,
		payBill,
		register,
		resetPassword,
		customContent,
		directDebit,
		moveWithin,
		profileUpdate,
		globalConfigModule,
		findCustomerNumber,
		forgotEmail,
		changeEmail,
	},
});

export const Store = store;
export type StoreType = typeof Store;
